import { Badge, Button, Card, CardBody, Skeleton, Tooltip } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { Globe } from 'lucide-react';
import type { OpsDomainBinding } from '@/api/operations';
import { formatDateTime } from '@/lib/format';

/* ── Type / status mappings ──────────────────────────────────────────────── */

type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';
type PillTone = 'success' | 'warning' | 'danger' | 'info' | 'muted' | 'neutral';

const TYPE_TONE: Record<string, BadgeTone> = {
  cdn: 'info',
  waap: 'warning',
  registrar: 'neutral',
  ssl: 'success',
  dns: 'neutral',
  livestream: 'success',
};

const STATUS_TONE: Record<string, PillTone> = {
  active: 'success',
  pending: 'warning',
  terminated: 'muted',
  suspended: 'danger',
  error: 'danger',
};

/* ── Vendor IDs extractor ────────────────────────────────────────────────── */

/**
 * Extracts known vendor ID fields from a domain binding's metadata JSONB.
 * Renders only fields that are present and non-empty so the cell stays clean.
 */
function VendorIds({ metadata }: { metadata?: Record<string, unknown> | null }) {
  if (!metadata) return <span style={{ color: 'var(--vn-fg-subtle)' }}>—</span>;
  const pairs: { key: string; label: string; value: string }[] = [];
  const tryAdd = (key: string, label: string) => {
    const v = metadata[key];
    if (v != null && String(v).length > 0) {
      pairs.push({ key, label, value: String(v) });
    }
  };
  tryAdd('waap_website_id', 'Website');
  tryAdd('waap_service_id', 'Service');
  tryAdd('cdn_domain_id', 'CDN ID');
  tryAdd('external_id', 'Ext ID');
  if (pairs.length === 0) return <span style={{ color: 'var(--vn-fg-subtle)' }}>—</span>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {pairs.map(({ key, label, value }) => (
        <Tooltip key={key} content={`${label}: ${value}`}>
          <span style={{ fontFamily: 'monospace', fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
            {label}: {value}
          </span>
        </Tooltip>
      ))}
    </div>
  );
}

/* ── Drift badge ─────────────────────────────────────────────────────────── */

function DriftBadge({ binding }: { binding: OpsDomainBinding }) {
  const hasDrift = Boolean(binding.drift_detected_at);
  if (!hasDrift) {
    return <span style={{ color: 'var(--vn-fg-subtle)' }}>—</span>;
  }
  const driftFields = Array.isArray(binding.drift_fields)
    ? (binding.drift_fields as string[]).join(', ')
    : 'unknown fields';
  return (
    <Tooltip content={`Detected at ${formatDateTime(binding.drift_detected_at)}: ${driftFields}`}>
      <Badge tone="warning">Drift</Badge>
    </Tooltip>
  );
}

/* ── Column definitions ──────────────────────────────────────────────────── */

const domainsColumns: ColumnDef<OpsDomainBinding, unknown>[] = [
  {
    id: 'domain_name',
    accessorKey: 'domain_name',
    header: 'Domain',
    cell: ({ row }) => (
      <span style={{ fontWeight: 500, color: 'var(--vn-fg-default)' }}>
        {row.original.domain_name}
      </span>
    ),
  },
  {
    id: 'type',
    accessorKey: 'type',
    header: 'Type',
    cell: ({ row }) => {
      const tone = TYPE_TONE[row.original.type] ?? 'neutral';
      return <Badge tone={tone}>{row.original.type.toUpperCase()}</Badge>;
    },
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const tone = STATUS_TONE[row.original.status] ?? 'neutral';
      return <StatusPill tone={tone}>{row.original.status}</StatusPill>;
    },
  },
  {
    id: 'vendor_ids',
    header: 'Vendor IDs',
    cell: ({ row }) => <VendorIds metadata={row.original.metadata} />,
  },
  {
    id: 'drift',
    header: 'Drift',
    cell: ({ row }) => <DriftBadge binding={row.original} />,
  },
  {
    id: 'updated_at',
    accessorKey: 'updated_at',
    header: 'Updated',
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
        {formatDateTime(row.original.updated_at)}
      </span>
    ),
  },
];

/* ── Props ────────────────────────────────────────────────────────────────── */

interface DomainsTabProps {
  data: OpsDomainBinding[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

/* ── Component ───────────────────────────────────────────────────────────── */

export function DomainsTab({ data, isLoading, isError, onRetry }: DomainsTabProps) {
  if (isError) {
    return (
      <Alert
        tone="danger"
        title="Failed to load domain bindings"
        actions={
          <Button size="sm" variant="ghost" onClick={onRetry}>
            Retry
          </Button>
        }
      />
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 24 }} />
            ))}
          </div>
        </CardBody>
      </Card>
    );
  }

  const rows = data ?? [];

  return (
    <Card>
      <CardBody style={{ padding: 0 }}>
        <DataTable
          columns={domainsColumns}
          data={rows}
          getRowId={(r) => r.id}
          dense
          emptyState={
            <EmptyState
              icon={<Globe size={24} />}
              title="No domain bindings"
              description="This service has no linked domains."
            />
          }
          footer={rows.length > 0 ? `${rows.length} domain${rows.length !== 1 ? 's' : ''}` : undefined}
        />
      </CardBody>
    </Card>
  );
}
